import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  addDays,
  format,
  isAfter,
  isSameDay,
  isValid,
  parse,
  parseISO,
  startOfDay,
  subDays,
} from "date-fns";
import {
  AlertTriangle,
  CalendarDays,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Download,
} from "lucide-react";

import {
  useFutureTipsQuery,
  useHistoricalResultsJWTQuery,
  useHistoricalResultsQuery,
  useResultsArchiveQuery,
} from "@/api/vercel";
import { Button, buttonVariants } from "@/components/ui/button";
import DatePicker from "@/components/ui/date-picker";
import { Card } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import ResultsArchiveTable from "@/components/ResultsArchiveTable";
import { cn } from "@/lib/utils";
import { logger } from "@/utils/logger";
import { formatTimeOfDay } from "@/utils/time-format";
import { downloadTextFile, objectsToCsv } from "@/utils/csv-export";
import { buildEarlyMailTipsLookupFromFutureTipsResponse } from "@/utils/future-tips-utils";
import { normalizeTrackNameForLookup } from "@/utils/future-tips-utils";

const TIP_POSITIONS = ["T1", "T2", "T3", "T4", "T5", "T6", "T7", "T8"];

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const FALLBACK_DAY_RANGE = 730;

const normalizeReturn = (value) => {
  if (value === null || value === undefined) {
    return 0;
  }

  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getTrackState = (trackName, trackDirectory) => {
  if (!trackName || !trackDirectory) return "";

  const directMatch = trackDirectory[trackName];
  if (directMatch?.State) {
    return directMatch.State;
  }

  const lowerName = trackName.toLowerCase();
  const fallbackMatch = Object.values(trackDirectory).find((entry) => {
    return (
      entry?.alt?.toLowerCase() === lowerName ||
      entry?.City?.toLowerCase() === lowerName
    );
  });

  return fallbackMatch?.State || "";
};

const buildRaceSummary = (raceData, raceNumber) => {
  const podium = Array.isArray(raceData?.podium) ? raceData.podium : [];
  const winnerLabel =
    podium.findIndex((result) => result === "winnerWinnerChickenDinner") + 1;

  return {
    number: raceNumber,
    meeting: raceData?.meetingName || raceData?.track || "",
    winnerTip: winnerLabel > 0 ? `T${winnerLabel}` : null,
    returns: normalizeReturn(
      raceData?.winReturns ?? raceData?.totalReturns ?? raceData?.returns,
    ),
    betfairWinner: raceData?.betfairResults?.winner?.name || null,
    podium,
    tipSummary:
      raceData?.predictionData?.firstFourData ||
      raceData?.earlyPredictionData?.firstFourData ||
      [],
  };
};

const normalizeSelectionId = (value) => {
  if (value === null || value === undefined) return null;
  const str = String(value).trim();
  return str.length ? str : null;
};

const normalizeHorseNumber = (value) => {
  if (value === null || value === undefined) return null;
  const str = String(value).trim();
  if (!str.length) return null;
  const withoutLeadingZeros = str.replace(/^0+/, "");
  return withoutLeadingZeros.length ? withoutLeadingZeros : "0";
};

const extractHorseNumber = (entry) => {
  if (!entry) return null;

  const nameFields = [
    entry.formattedName,
    entry.selectionName,
    entry.runnerName,
    entry.name,
  ];

  for (const field of nameFields) {
    if (typeof field === "string" && field.trim().length) {
      const leading = field.match(/^(\d+)[\s.\-_:]/);
      const candidate = leading ? leading[1] : field.split(".")[0];
      const normalized = normalizeHorseNumber(candidate);
      if (normalized) return normalized;
    }
  }

  const fallbackFields = [
    entry.horseNumber,
    entry.runnerNumber,
    entry.raceNumber,
    entry.number,
  ];

  for (const field of fallbackFields) {
    const normalized = normalizeHorseNumber(field);
    if (normalized) return normalized;
  }

  return null;
};

const formatTipRoi = (returns, bets) => {
  if (!bets) return "ROI —";
  const roi = ((returns - bets) / bets) * 100;
  const sign = roi >= 0 ? "+" : "-";
  return `ROI ${sign}${Math.abs(roi).toFixed(1)}%`;
};

export default function AnalysisDatePage({ defaultToToday = false }) {
  const { dateParam, slug } = useParams();
  const routeParam = dateParam ?? slug ?? null;
  const navigate = useNavigate();
  const [trackDirectory, setTrackDirectory] = useState({});
  const [showMaxHedge, setShowMaxHedge] = useState(false);
  const [maxHedgeWinRate, setMaxHedgeWinRate] = useState(0.8);
  const [showTrackBreakdown, setShowTrackBreakdown] = useState(false);
  const [insightIndex, setInsightIndex] = useState(0);
  const [useJwtFallback, setUseJwtFallback] = useState(false);
  const [isArchiveFullWidth, setIsArchiveFullWidth] = useState(false);
  const [visibleArchiveRaces, setVisibleArchiveRaces] = useState([]);

  const today = useMemo(() => startOfDay(new Date()), []);

  useEffect(() => {
    let isMounted = true;

    const loadTrackDirectory = async () => {
      try {
        const response = await fetch(
          "/data/horseracing/tracks/track-names.json",
        );

        if (!response.ok) {
          throw new Error("Failed to load track metadata");
        }

        const data = await response.json();
        if (isMounted) {
          setTrackDirectory(data);
        }
      } catch (err) {
        console.error("Error loading track metadata:", err);
      }
    };

    loadTrackDirectory();

    return () => {
      isMounted = false;
    };
  }, []);

  const resolvedDate = useMemo(() => {
    if (defaultToToday || routeParam === "today") {
      return new Date();
    }

    if (routeParam && DATE_REGEX.test(routeParam)) {
      const parsed = parseISO(routeParam);
      return isValid(parsed) ? parsed : null;
    }

    return null;
  }, [routeParam, defaultToToday]);

  const isoDateFromParam = useMemo(() => {
    if (routeParam && DATE_REGEX.test(routeParam)) {
      return routeParam;
    }
    return "";
  }, [routeParam]);

  const dateError = !resolvedDate && !defaultToToday;
  const isoDate = isoDateFromParam || (resolvedDate ? format(resolvedDate, "yyyy-MM-dd") : "");
  const queryDate = useMemo(() => {
    if (isoDateFromParam) {
      const [yyyy, mm, dd] = isoDateFromParam.split("-");
      return `${dd}-${mm}-${yyyy}`;
    }
    return resolvedDate ? format(resolvedDate, "dd-MM-yyyy") : "";
  }, [isoDateFromParam, resolvedDate]);
  const normalizedResolvedDate = resolvedDate ? startOfDay(resolvedDate) : null;
  const queryEnabled = !!queryDate && !dateError;
  const readableDate = resolvedDate
    ? format(resolvedDate, "EEEE, MMMM d, yyyy")
    : "";

  const shouldShowEarlyMailTips = useMemo(() => {
    // Only show this block on explicit /analysis/YYYY-MM-DD (not /analysis/today).
    return Boolean(isoDateFromParam);
  }, [isoDateFromParam]);

  const { data: futureTipsResponse } = useFutureTipsQuery({
    date: queryDate,
    enabled: queryEnabled && shouldShowEarlyMailTips,
    staleTime: 15 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const earlyMailTipsLookup = useMemo(() => {
    if (!futureTipsResponse) return new Map();
    try {
      return buildEarlyMailTipsLookupFromFutureTipsResponse(futureTipsResponse);
    } catch (err) {
      logger.error("[AnalysisDatePage] Failed to build early mail tips lookup", err);
      return new Map();
    }
  }, [futureTipsResponse]);

  const hasEarlyMailTips = useMemo(() => {
    if (!earlyMailTipsLookup || !(earlyMailTipsLookup instanceof Map)) return false;
    return earlyMailTipsLookup.size > 0;
  }, [earlyMailTipsLookup]);

  // Surface key date + query calculations so we can trace route resolution in the browser console
  useEffect(() => {
    logger.log("[AnalysisDatePage] date resolution", {
      routeParam,
      isoDate,
      queryDate,
      queryEnabled,
      useJwtFallback,
    });
  }, [routeParam, isoDate, queryDate, queryEnabled, useJwtFallback]);

  const { data: availableDatesResponse } = useResultsArchiveQuery({
    action: "get_results",
    date: queryDate,
    staleTime: 2 * 60 * 60 * 1000,
    gcTime: 6 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: queryEnabled,
  });

  const fallbackDateSet = useMemo(() => {
    const set = new Set();
    for (let i = 0; i <= FALLBACK_DAY_RANGE; i += 1) {
      const target = subDays(today, i);
      set.add(format(target, "yyyy-MM-dd"));
    }
    return set;
  }, [today]);

  const extractedDateSet = useMemo(() => {
    const set = new Set();
    if (!availableDatesResponse?.data) return set;

    const responseArray = Array.isArray(availableDatesResponse.data)
      ? availableDatesResponse.data
      : Array.isArray(availableDatesResponse?.data?.data)
        ? availableDatesResponse.data.data
        : [];

    responseArray.forEach((entry) => {
      const candidate =
        entry?.date ||
        entry?.dateKey ||
        (typeof entry === "string" ? entry : null);

      if (typeof candidate === "string") {
        if (/^\d{4}-\d{2}-\d{2}$/.test(candidate)) {
          set.add(candidate);
        } else if (/^\d{2}-\d{2}-\d{4}$/.test(candidate)) {
          const parsed = parse(candidate, "dd-MM-yyyy", new Date());
          if (isValid(parsed)) {
            set.add(format(parsed, "yyyy-MM-dd"));
          }
        }
      }
    });

    return set;
  }, [availableDatesResponse]);

  const availableDateSet = useMemo(() => {
    const combined = new Set(fallbackDateSet);
    extractedDateSet.forEach((value) => combined.add(value));
    if (normalizedResolvedDate) {
      combined.add(format(normalizedResolvedDate, "yyyy-MM-dd"));
    }
    return combined;
  }, [fallbackDateSet, extractedDateSet, normalizedResolvedDate]);

  const pickerSelectedDate = normalizedResolvedDate || today;

  const handleNavigateByOffset = (offsetDays) => {
    if (!normalizedResolvedDate) return;
    const target =
      offsetDays < 0
        ? subDays(normalizedResolvedDate, Math.abs(offsetDays))
        : addDays(normalizedResolvedDate, offsetDays);

    if (isAfter(target, today)) {
      return;
    }

    const isoTarget = format(target, "yyyy-MM-dd");
    if (isSameDay(target, today)) {
      navigate("/analysis/today");
      return;
    }

    navigate(`/analysis/${isoTarget}`);
  };

  const handleDateSelect = (date) => {
    if (!date) return;
    const normalized = startOfDay(date);

    if (isAfter(normalized, today)) {
      return;
    }

    const isoTarget = format(normalized, "yyyy-MM-dd");
    if (isSameDay(normalized, today)) {
      navigate("/analysis/today");
      return;
    }

    navigate(`/analysis/${isoTarget}`);
  };

  const handleJumpToToday = () => {
    navigate("/analysis/today");
  };

  const prevDisabled = !normalizedResolvedDate;
  const nextDisabled =
    !normalizedResolvedDate || isAfter(addDays(normalizedResolvedDate, 1), today);
  const todayDisabled = normalizedResolvedDate
    ? isSameDay(normalizedResolvedDate, today)
    : false;

  const historicalQueryOptions = useMemo(
    () => ({
      date: queryDate,
      fetchAll: false,
      enabled: queryEnabled,
      refetchOnWindowFocus: false,
    }),
    [queryDate, queryEnabled],
  );

  const {
    data: historicalResults,
    isLoading: isPrimaryLoading,
    error: primaryError,
  } = useHistoricalResultsQuery(historicalQueryOptions);

  useEffect(() => {
    setUseJwtFallback(false);
  }, [queryDate]);

  useEffect(() => {
    if (!queryEnabled) return;
    if (useJwtFallback) return;
    if (isPrimaryLoading) return;
    const statusError = historicalResults?.status === "error";
    if (primaryError || statusError) {
      setUseJwtFallback(true);
    }
  }, [queryEnabled, useJwtFallback, isPrimaryLoading, primaryError, historicalResults]);

  const {
    data: jwtHistoricalResults,
    isLoading: isJwtLoading,
    error: jwtError,
  } = useHistoricalResultsJWTQuery({
    date: queryDate,
    fetchAll: false,
    enabled: queryEnabled && useJwtFallback,
    refetchOnWindowFocus: false,
  });

  const isLoading = queryEnabled
    ? useJwtFallback
      ? isJwtLoading
      : isPrimaryLoading
    : false;

  const primaryStatusError =
    historicalResults?.status === "error" ? historicalResults?.message : null;
  const fallbackStatusError =
    jwtHistoricalResults?.status === "error" ? jwtHistoricalResults?.message : null;

  const statusErrorMessage = useJwtFallback ? fallbackStatusError : primaryStatusError;

  const queryError = useJwtFallback ? jwtError : primaryError;

  const activeHistoricalResults =
    useJwtFallback && jwtHistoricalResults ? jwtHistoricalResults : historicalResults;

  const displayError = useMemo(() => {
    if (queryError) return queryError;
    if (statusErrorMessage) {
      return new Error(statusErrorMessage);
    }
    return null;
  }, [queryError, statusErrorMessage]);

  // Log query lifecycle so we can see whether the primary or fallback path is stuck
  useEffect(() => {
    logger.log("[AnalysisDatePage] historical results query state", {
      isPrimaryLoading,
      primaryError,
      primaryStatus: historicalResults?.status,
      useJwtFallback,
      isJwtLoading,
      jwtError,
      jwtStatus: jwtHistoricalResults?.status,
      hasPrimaryData: !!historicalResults?.data && Object.keys(historicalResults.data).length > 0,
      hasJwtData: !!jwtHistoricalResults?.data && Object.keys(jwtHistoricalResults.data).length > 0,
    });
  }, [
    historicalResults,
    isPrimaryLoading,
    primaryError,
    useJwtFallback,
    isJwtLoading,
    jwtError,
    jwtHistoricalResults,
  ]);

  const processedData = useMemo(() => {
    const rawResults = activeHistoricalResults?.data || {};
    const entries = Object.entries(rawResults);

    const defaultTotals = {
      trackCount: 0,
      raceCount: 0,
      winners: 0,
      netReturn: 0,
    };

    if (!entries.length) {
      return {
        tracks: [],
        totals: defaultTotals,
        archiveRows: [],
      };
    }

    const trackMap = new Map();
    let aggregateWins = 0;
    let aggregateReturns = 0;
    const archiveRows = [];

    entries.forEach(([key, raceData]) => {
      const parts = key.split("-");
      if (parts.length < 5) return;

      const raceNumber = parts[parts.length - 4];
      const trackName = parts.slice(0, parts.length - 4).join(" ");

      if (!trackMap.has(trackName)) {
        trackMap.set(trackName, {
          name: trackName,
          state: getTrackState(trackName, trackDirectory),
          totalRaces: 0,
          totalReturns: 0,
          totalWins: 0,
          tipStats: TIP_POSITIONS.reduce((acc, tipKey) => {
            acc[tipKey] = { bets: 0, wins: 0, returns: 0 };
            return acc;
          }, {}),
        });
      }

      const track = trackMap.get(trackName);
      track.totalRaces += 1;

      const raceSummary = buildRaceSummary(raceData, raceNumber);
      track.totalReturns += raceSummary.returns;

      if (raceSummary.winnerTip) {
        track.totalWins += 1;
        aggregateWins += 1;
      }

      aggregateReturns += raceSummary.returns;

      const betfairWinner = raceData.betfairResults?.winner || {};
      const winnerSelectionId = normalizeSelectionId(betfairWinner.selectionId);
      let winnerHorseNumber = extractHorseNumber(betfairWinner);
      const rawWinPrice = parseFloat(betfairWinner.startingPrice);
      const winPrice = Number.isFinite(rawWinPrice) ? rawWinPrice : 0;

      const earlyTips = Array.isArray(raceData.earlyPredictionData?.firstFourData)
        ? raceData.earlyPredictionData.firstFourData
        : [];
      const raceTips = Array.isArray(raceData.predictionData?.firstFourData)
        ? raceData.predictionData.firstFourData
        : [];
      const tipData = [...earlyTips, ...raceTips];

      if (!winnerHorseNumber && winnerSelectionId) {
        const matchingTip = tipData.find(
          (tip) => normalizeSelectionId(tip?.selectionId) === winnerSelectionId,
        );
        if (matchingTip) {
          winnerHorseNumber = extractHorseNumber(matchingTip);
        }
      }

      tipData.forEach((tip, idx) => {
        if (!tip) return;

        const tipKey = TIP_POSITIONS[idx];
        const tipStats = track.tipStats[tipKey];
        if (!tipStats) return;

        const hasTipData =
          tip.formattedName || tip.selectionId || tip.runnerName || tip.name;
        if (!hasTipData) return;

        tipStats.bets += 1;

        const tipSelectionId = normalizeSelectionId(tip?.selectionId);
        const tipHorseNumber = extractHorseNumber(tip);
        const matchesById =
          winnerSelectionId &&
          tipSelectionId &&
          tipSelectionId === winnerSelectionId;
        const matchesByNumber =
          !matchesById &&
          winnerHorseNumber &&
          tipHorseNumber &&
          tipHorseNumber === winnerHorseNumber;

        if (matchesById || matchesByNumber) {
          tipStats.wins += 1;
          tipStats.returns += winPrice;
        }
      });

      const normalizedRace = {
        id: raceData.id || key,
        meetingName: raceData.meetingName || trackName,
        ...raceData,
      };
      archiveRows.push(normalizedRace);
    });

    const tracks = Array.from(trackMap.values()).map((track) => ({
      ...track,
      roi:
        track.totalRaces > 0
          ? ((track.totalReturns - track.totalRaces) / track.totalRaces) * 100
          : 0,
      net: track.totalReturns - track.totalRaces,
    }));

    tracks.sort((a, b) => {
      if (b.totalWins === a.totalWins) {
        return a.name.localeCompare(b.name);
      }
      return b.totalWins - a.totalWins;
    });

    return {
      tracks,
      totals: {
        trackCount: tracks.length,
        raceCount: archiveRows.length,
        winners: aggregateWins,
        netReturn: aggregateReturns - archiveRows.length,
      },
      archiveRows,
    };
  }, [activeHistoricalResults, trackDirectory]);

  const { tracks, totals, archiveRows } = processedData;
  const emptyState = !isLoading && archiveRows.length === 0;
  const archiveData = useMemo(
    () => ({
      status: "success",
      count: archiveRows.length,
      data: archiveRows,
    }),
    [archiveRows],
  );

  const buildResultsArchiveCsvRow = useCallback((race) => {
    const raceId = race?.id || "";
    const parts = typeof raceId === "string" ? raceId.split("-") : [];
    const raceNumber = parts.length >= 4 ? parts[parts.length - 4] : "";

    const toIsoDate = (value) => {
      if (!value || typeof value !== "string") return "";
      const trimmed = value.trim();
      if (!trimmed.length) return "";

      // Already ISO
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

      // dd-mm-yyyy
      if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(trimmed)) {
        const [ddRaw, mmRaw, yyyy] = trimmed.split("-");
        const dd = String(ddRaw).padStart(2, "0");
        const mm = String(mmRaw).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      }

      // dd/mm/yyyy
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
        const [ddRaw, mmRaw, yyyy] = trimmed.split("/");
        const dd = String(ddRaw).padStart(2, "0");
        const mm = String(mmRaw).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      }

      return trimmed;
    };

    const getRaceDate = () => {
      if (race?.date && typeof race.date === "string") return toIsoDate(race.date);
      if (parts.length >= 3) {
        const dd = parts[parts.length - 3];
        const mm = parts[parts.length - 2];
        const yyyy = parts[parts.length - 1];
        if (/^\d{1,2}$/.test(dd) && /^\d{1,2}$/.test(mm) && /^\d{4}$/.test(yyyy)) {
          return `${yyyy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
        }
      }
      return "";
    };

    const parseDateTimeValue = (value) => {
      if (!value) return null;
      if (typeof value === "number") {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? null : d;
      }
      if (typeof value === "object") {
        if (typeof value.seconds === "number") {
          const d = new Date(
            value.seconds * 1000 + (value.nanoseconds ? value.nanoseconds / 1e6 : 0),
          );
          return Number.isNaN(d.getTime()) ? null : d;
        }
        if (typeof value._seconds === "number") {
          const d = new Date(
            value._seconds * 1000 + (value._nanoseconds ? value._nanoseconds / 1e6 : 0),
          );
          return Number.isNaN(d.getTime()) ? null : d;
        }
      }
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed.length) return null;
        if (/^\d{1,2}:\d{2}/.test(trimmed)) return trimmed;
        const normalized = trimmed.includes("T") ? trimmed : trimmed.replace(" ", "T");
        const d = new Date(normalized);
        return Number.isNaN(d.getTime()) ? null : d;
      }
      return null;
    };

    const mergeDateAndTime = (baseDate, timeStr) => {
      if (!baseDate || !timeStr) return null;
      const [hh, mm] = timeStr.split(":");
      if (hh === undefined || mm === undefined) return null;
      const merged = new Date(baseDate);
      const hours = Number(hh);
      const minutes = Number(mm);
      if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
      merged.setHours(hours, minutes, 0, 0);
      return merged;
    };

    const getRaceDateTime = () => {
      const dateStr = getRaceDate();
      const baseDate = dateStr ? new Date(`${dateStr}T00:00:00`) : null;
      const safeBase = baseDate && !Number.isNaN(baseDate.getTime()) ? baseDate : null;
      const candidates = [
        race?.predictionData?.marketStartTime,
        race?.earlyPredictionData?.marketStartTime,
        race?.betfairResults?.raceStartTime,
        race?.betfairResults?.raceStart,
        race?.betfairResults?.startTime,
        race?.betfairResults?.marketStartTime,
        race?.marketStartTime,
        race?.startTime,
      ];

      for (const candidate of candidates) {
        if (!candidate) continue;
        if (typeof candidate === "string" && /^\d{1,2}:\d{2}/.test(candidate.trim()) && safeBase) {
          const merged = mergeDateAndTime(safeBase, candidate.trim());
          if (merged) return merged;
        }
        const parsed = parseDateTimeValue(candidate);
        if (parsed instanceof Date && !Number.isNaN(parsed.getTime())) return parsed;
      }
      return safeBase;
    };

    // Site-wide time format: "1:10 pm" in Sydney time
    const getRaceTimeDisplay = () => {
      const dt = getRaceDateTime();
      if (!dt || Number.isNaN(dt.getTime())) return "—";
      return formatTimeOfDay(dt) || "—";
    };

    const earlyTips = Array.isArray(race?.earlyPredictionData?.firstFourData)
      ? race.earlyPredictionData.firstFourData
      : [];
    const raceTips = Array.isArray(race?.predictionData?.firstFourData)
      ? race.predictionData.firstFourData
      : [];

    const trackNameDisplay = race?.meetingName || parts.slice(0, parts.length - 4).join(" ");

    const earlyMailTipNumbers = (() => {
      if (!shouldShowEarlyMailTips || !hasEarlyMailTips) {
        return ["-", "-", "-", "-"];
      }
      if (!trackNameDisplay || !raceNumber) return ["-", "-", "-", "-"];
      const key = `${normalizeTrackNameForLookup(trackNameDisplay)}|${raceNumber}`;
      const tips = earlyMailTipsLookup?.get?.(key) || [];
      return [
        tips[0]?.formattedName?.split(".")[0] || "-",
        tips[1]?.formattedName?.split(".")[0] || "-",
        tips[2]?.formattedName?.split(".")[0] || "-",
        tips[3]?.formattedName?.split(".")[0] || "-",
      ];
    })();

    const tipNumbers = [
      earlyTips[0]?.formattedName?.split(".")[0] || "-",
      earlyTips[1]?.formattedName?.split(".")[0] || "-",
      earlyTips[2]?.formattedName?.split(".")[0] || "-",
      earlyTips[3]?.formattedName?.split(".")[0] || "-",
      raceTips[0]?.formattedName?.split(".")[0] || "-",
      raceTips[1]?.formattedName?.split(".")[0] || "-",
      raceTips[2]?.formattedName?.split(".")[0] || "-",
      raceTips[3]?.formattedName?.split(".")[0] || "-",
    ];

    const uniqueTipsCount = new Set(tipNumbers.filter((t) => t && t !== "-")).size;

    const calcReciprocalSum = (tips) => {
      if (!Array.isArray(tips)) return "-";
      let sum = 0;
      tips.forEach((tip) => {
        if (!tip?.formattedName) return;
        const odds = tip.lastPriceTraded || tip.currentPrice;
        const n = parseFloat(odds);
        if (Number.isFinite(n) && n > 0) sum += 1 / n;
      });
      return sum > 0 ? sum.toFixed(3) : "-";
    };

    const morningMaxHedge = calcReciprocalSum(earlyTips);
    const raceTimeMaxHedge = calcReciprocalSum(raceTips);

    const combinedMaxHedge = (() => {
      const unique = new Map();
      earlyTips.forEach((tip) => {
        if (!tip?.formattedName) return;
        const horseNumber = tip.formattedName.split(".")[0];
        const odds = parseFloat(tip.lastPriceTraded || tip.currentPrice);
        if (horseNumber && horseNumber !== "-" && Number.isFinite(odds) && odds > 0) {
          unique.set(horseNumber, odds);
        }
      });
      raceTips.forEach((tip) => {
        if (!tip?.formattedName) return;
        const horseNumber = tip.formattedName.split(".")[0];
        const odds = parseFloat(tip.lastPriceTraded || tip.currentPrice);
        if (horseNumber && horseNumber !== "-" && Number.isFinite(odds) && odds > 0) {
          unique.set(horseNumber, odds);
        }
      });
      if (unique.size === 0) return "-";
      let sum = 0;
      unique.forEach((odds) => {
        sum += 1 / odds;
      });
      return sum.toFixed(3);
    })();

    const betfairResults = race?.betfairResults || {};
    const winner = betfairResults?.winner;
    const allTips = [...earlyTips, ...raceTips];
    const getHorseNumberBySelectionId = (selectionId) => {
      if (!selectionId) return "-";
      const found = allTips.find((tip) => tip?.selectionId === selectionId);
      return found?.formattedName?.split(".")[0] || "-";
    };
    const firstPlace = winner ? getHorseNumberBySelectionId(winner.selectionId) : "-";
    const winPriceRaw = winner?.startingPrice ?? "";
    const winPrice = (() => {
      const n = parseFloat(winPriceRaw);
      return Number.isFinite(n) ? n : winPriceRaw || "";
    })();

    const placeGettersAll = Array.isArray(betfairResults?.placeGetters)
      ? betfairResults.placeGetters
      : [];
    const winnerSelectionIdNorm = normalizeSelectionId(winner?.selectionId);
    const orderedPlaceSlots = (() => {
      if (!placeGettersAll.length) return [];
      const winnerEntry = winnerSelectionIdNorm
        ? placeGettersAll.find((p) => normalizeSelectionId(p?.selectionId) === winnerSelectionIdNorm)
        : null;
      const others = winnerSelectionIdNorm
        ? placeGettersAll.filter((p) => normalizeSelectionId(p?.selectionId) !== winnerSelectionIdNorm)
        : placeGettersAll.slice(1);
      return winnerEntry ? [winnerEntry, ...others] : placeGettersAll;
    })();
    const parseMaybeNumber = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : v ?? "";
    };

    const p1 = parseMaybeNumber(orderedPlaceSlots[0]?.startingPrice);
    const p2 = parseMaybeNumber(orderedPlaceSlots[1]?.startingPrice);
    const p3 = parseMaybeNumber(orderedPlaceSlots[2]?.startingPrice);

    const horses = betfairResults?.numberOfRunners ?? "-";

    return {
      date: getRaceDate(),
      time: getRaceTimeDisplay(),
      track: trackNameDisplay,
      raceNumber,
      horses,
      e1: earlyMailTipNumbers[0],
      e2: earlyMailTipNumbers[1],
      e3: earlyMailTipNumbers[2],
      e4: earlyMailTipNumbers[3],
      t1: tipNumbers[0],
      t2: tipNumbers[1],
      t3: tipNumbers[2],
      t4: tipNumbers[3],
      morningMaxHedge,
      t5: tipNumbers[4],
      t6: tipNumbers[5],
      t7: tipNumbers[6],
      t8: tipNumbers[7],
      raceTimeMaxHedge,
      uniqueTips: uniqueTipsCount,
      first: firstPlace,
      combinedMaxHedge,
      win: winPrice,
      p1,
      p2,
      p3,
    };
  }, [earlyMailTipsLookup, hasEarlyMailTips, shouldShowEarlyMailTips]);

  const handleDownloadCsv = useCallback(() => {
    const races = Array.isArray(visibleArchiveRaces) && visibleArchiveRaces.length
      ? visibleArchiveRaces
      : archiveRows;
    if (!races.length) return;

    const rows = races.map(buildResultsArchiveCsvRow);
    const columns = [
      { key: "date", label: "Date" },
      { key: "time", label: "Time" },
      { key: "track", label: "Track" },
      { key: "raceNumber", label: "Race #" },
      { key: "horses", label: "Horses" },
      ...(shouldShowEarlyMailTips && hasEarlyMailTips
        ? [
            { key: "e1", label: "E1" },
            { key: "e2", label: "E2" },
            { key: "e3", label: "E3" },
            { key: "e4", label: "E4" },
          ]
        : []),
      { key: "t1", label: "T1" },
      { key: "t2", label: "T2" },
      { key: "t3", label: "T3" },
      { key: "t4", label: "T4" },
      ...(showMaxHedge ? [{ key: "morningMaxHedge", label: "M.Odds" }] : []),
      { key: "t5", label: "T5" },
      { key: "t6", label: "T6" },
      { key: "t7", label: "T7" },
      { key: "t8", label: "T8" },
      ...(showMaxHedge ? [{ key: "raceTimeMaxHedge", label: "R.Odds" }] : []),
      { key: "uniqueTips", label: "T.Tot" },
      { key: "first", label: "1st" },
      ...(showMaxHedge ? [{ key: "combinedMaxHedge", label: "Other Max Hedge" }] : []),
      { key: "win", label: "Win" },
      { key: "p1", label: "P1" },
      { key: "p2", label: "P2" },
      { key: "p3", label: "P3" },
    ];

    const csv = objectsToCsv(rows, columns);
    downloadTextFile({
      filename: `lexi-analysis-${isoDate || "date"}-archive.csv`,
      text: csv,
      mimeType: "text/csv;charset=utf-8",
    });
  }, [
    archiveRows,
    buildResultsArchiveCsvRow,
    hasEarlyMailTips,
    isoDate,
    shouldShowEarlyMailTips,
    showMaxHedge,
    visibleArchiveRaces,
  ]);

  const trackInsights = useMemo(() => {
    if (!tracks.length) return [];

    const validTracks = tracks.filter((track) => Number.isFinite(track.roi));
    const highestROITrack = validTracks.reduce(
      (best, track) => (track.roi > (best?.roi ?? -Infinity) ? track : best),
      null,
    );

    const mostWinsTrack = tracks.reduce(
      (best, track) => (track.totalWins > (best?.totalWins ?? -Infinity) ? track : best),
      null,
    );

    const tipAggregates = TIP_POSITIONS.reduce((acc, key) => {
      acc[key] = { bets: 0, wins: 0, returns: 0 };
      return acc;
    }, {});

    tracks.forEach((track) => {
      TIP_POSITIONS.forEach((key) => {
        const stats = track.tipStats?.[key];
        if (stats) {
          tipAggregates[key].bets += stats.bets || 0;
          tipAggregates[key].wins += stats.wins || 0;
          tipAggregates[key].returns += stats.returns || 0;
        }
      });
    });

    const tipEntries = TIP_POSITIONS.map((key) => {
      const aggregate = tipAggregates[key];
      const bets = aggregate.bets || 0;
      const returns = aggregate.returns || 0;
      const roi = bets > 0 ? ((returns - bets) / bets) * 100 : -Infinity;
      return { key, roi, wins: aggregate.wins, bets };
    });

    const bestTipByROI = tipEntries.reduce(
      (best, entry) => (entry.roi > (best?.roi ?? -Infinity) ? entry : best),
      null,
    );

    const bestTipByWins = tipEntries.reduce(
      (best, entry) => (entry.wins > (best?.wins ?? -Infinity) ? entry : best),
      null,
    );

    const insights = [];

    if (highestROITrack && Number.isFinite(highestROITrack.roi)) {
      insights.push(
        `${highestROITrack.name} led ROI at ${highestROITrack.roi >= 0 ? "+" : "-"}${Math.abs(
          highestROITrack.roi,
        ).toFixed(1)}%.`,
      );
    }

    if (mostWinsTrack) {
      insights.push(`${mostWinsTrack.name} produced ${mostWinsTrack.totalWins} winning tips.`);
    }

    if (bestTipByROI && Number.isFinite(bestTipByROI.roi) && bestTipByROI.roi !== -Infinity) {
      insights.push(
        `${bestTipByROI.key} line returned ${bestTipByROI.roi >= 0 ? "+" : "-"}${Math.abs(bestTipByROI.roi).toFixed(
          1,
        )}% ROI.`,
      );
    }

    if (bestTipByWins) {
      insights.push(`${bestTipByWins.key} landed ${bestTipByWins.wins} winners.`);
    }

    return insights;
  }, [tracks]);

  useEffect(() => {
    setInsightIndex(0);
  }, [trackInsights.length]);

  useEffect(() => {
    if (trackInsights.length <= 1) return;
    const interval = setInterval(() => {
      setInsightIndex((prev) => (prev + 1) % trackInsights.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [trackInsights]);

  return (
    <div className="space-y-8">
      <Helmet>
        <title>{resolvedDate ? `Racing Analysis | ${readableDate}` : "Racing Analysis by Date"}</title>
        {resolvedDate && (
          <meta
            name="description"
            content={`Track-by-track analysis of Lexi Tips coverage for ${readableDate}.`}
          />
        )}
      </Helmet>

      <div className="mt-16 flex flex-col gap-3">
        <nav className="flex items-center space-x-2 text-sm text-gray-600">
          <Link to="/analysis" className="hover:text-[#F30099] transition-colors">
            Analysis
          </Link>
          <span>/</span>
          <span className="text-gray-900 font-medium">
            {resolvedDate ? readableDate : "Date"}
          </span>
        </nav>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900">
              {resolvedDate ? `Racing Analysis for ${readableDate}` : "Racing Analysis by Date"}
            </h1>
          </div>

          {!dateError && (
            <div className="flex flex-wrap items-center gap-2 md:justify-end">
              <Button
                type="button"
                className={cn(
                  buttonVariants({ variant: "outline", size: "icon" }),
                  "border-gray-200",
                )}
                onClick={() => handleNavigateByOffset(-1)}
                disabled={prevDisabled}
                aria-label="Previous day"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <div className="min-w-[220px]">
                <DatePicker
                  selected={pickerSelectedDate}
                  onChange={handleDateSelect}
                  maxDate={today}
                  availableDates={availableDateSet}
                  className="w-full"
                />
              </div>

              <Button
                type="button"
                className={cn(
                  buttonVariants({ variant: "outline", size: "icon" }),
                  "border-gray-200",
                )}
                onClick={() => handleNavigateByOffset(1)}
                disabled={nextDisabled}
                aria-label="Next day"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>

              <Button
                type="button"
                className={cn(
                  buttonVariants({ variant: "outline", size: "sm" }),
                  "border-gray-200",
                )}
                onClick={handleJumpToToday}
                disabled={todayDisabled}
              >
                Jump to today
              </Button>
            </div>
          )}
        </div>
      </div>

      {dateError && (
        <Card className="p-6 border border-amber-200 bg-amber-50">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-800 mb-1">
                We could not recognise that date.
              </p>
              <p className="text-amber-800 text-sm">
                Please use the format <code>YYYY-MM-DD</code>, for example{" "}
                <Link to="/analysis/2025-02-01" className="text-[#F30099] underline-offset-2 underline">
                  /analysis/2025-02-01
                </Link>
                , or jump to{" "}
                <Link to="/analysis/today" className="text-[#F30099] underline-offset-2 underline">
                  today&apos;s coverage
                </Link>
                .
              </p>
            </div>
          </div>
        </Card>
      )}

      {!dateError && isLoading && (
        <div className="flex items-center justify-center py-20">
          <Spinner />
        </div>
      )}

      {!dateError && !isLoading && displayError && (
        <Card className="p-6 border border-red-200 bg-red-50">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <p className="font-semibold text-red-800 mb-1">
                Unable to load the selected date
              </p>
              <p className="text-red-800 text-sm">
                {displayError?.message || "Please try again or pick another date."}
              </p>
            </div>
          </div>
        </Card>
      )}

      {!dateError && !isLoading && !displayError && (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <Card className="p-5 border border-gray-100 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                  <CalendarDays className="w-5 h-5 text-gray-700" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Tracks Covered
                  </p>
                  <p className="text-2xl font-bold text-gray-900">{totals.trackCount}</p>
                </div>
              </div>
            </Card>
            <Card className="p-5 border border-gray-100 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-gray-700" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Races Analysed
                  </p>
                  <p className="text-2xl font-bold text-gray-900">{totals.raceCount}</p>
                </div>
              </div>
            </Card>
            <Card className="p-5 border border-gray-100 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Winning Tips
                  </p>
                  <p className="text-2xl font-bold text-gray-900">{totals.winners}</p>
                </div>
              </div>
            </Card>
            <Card className="p-5 border border-gray-100 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">
                    Net Returns (at $1 stakes)
                  </p>
                  <p className={`text-2xl font-bold ${totals.netReturn >= 0 ? "text-green-600" : "text-red-600"}`}>
                    {totals.netReturn >= 0 ? "+" : "-"}${Math.abs(totals.netReturn).toFixed(2)}
                  </p>
                  {trackInsights.length > 0 && (
                    <p className="text-xs text-gray-600 mt-1 transition-opacity">
                      {trackInsights[insightIndex]}
                    </p>
                  )}
                </div>
              </div>
            </Card>
          </div>

          {/* {tracks.length > 0 && (
            <div className="mt-8">
              <Card className="border border-gray-200 shadow-sm">
                <div className="p-5 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Track breakdown</h2>
                    <p className="text-sm text-gray-600">
                      Performance of each tip line per track (assumes $1 win stake per tip).
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowTrackBreakdown((prev) => !prev)}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#F30099] hover:text-[#c20078] transition-colors"
                  >
                    {showTrackBreakdown ? "Hide breakdown" : "Show breakdown"}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`w-4 h-4 transform transition-transform ${showTrackBreakdown ? "rotate-180" : ""}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
                {showTrackBreakdown && (
                  <div className="px-5 pb-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {tracks.map((track) => (
                      <Card key={`${isoDate}-${track.name}`} className="p-5 border border-gray-100 shadow-sm">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {track.name}
                              {track.state && (
                                <span className="ml-1 text-sm font-medium text-gray-500">
                                  ({track.state})
                                </span>
                              )}
                            </h3>
                            <p className="text-xs text-gray-500">
                              {track.totalRaces} races · {track.totalWins} winning tips
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs uppercase tracking-wide text-gray-500">ROI</p>
                            <p className={`text-lg font-bold ${track.roi >= 0 ? "text-green-600" : "text-red-600"}`}>
                              {track.roi >= 0 ? "+" : "-"}
                              {Math.abs(track.roi).toFixed(1)}%
                            </p>
                          </div>
                        </div>

                        <div className="mt-4 grid grid-cols-2 gap-3">
                          {TIP_POSITIONS.map((tipKey, idx) => {
                            const tipStats = track.tipStats?.[tipKey] || {
                              bets: 0,
                              wins: 0,
                              returns: 0,
                            };
                            const palette =
                              idx < 4
                                ? "bg-blue-50 border-blue-100 text-blue-900"
                                : "bg-purple-50 border-purple-100 text-purple-900";

                            return (
                              <div
                                key={`${track.name}-${tipKey}`}
                                className={`rounded-lg border px-3 py-2 ${palette}`}
                              >
                                <div className="flex items-center justify-between text-xs font-semibold">
                                  <span>{tipKey}</span>
                                  <span>{tipStats.wins ?? 0} wins</span>
                                </div>
                                <div className="text-[11px] text-gray-600 mt-1">
                                  {tipStats.bets} bets · {formatTipRoi(tipStats.returns, tipStats.bets)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          )} */}

          {emptyState ? (
            <Card className="p-8 border border-blue-100 bg-blue-50">
              <div className="flex flex-col gap-4 text-center">
                <div className="flex items-center justify-center gap-3 text-blue-700">
                  <AlertTriangle className="w-5 h-5" />
                  <p className="font-semibold">No tracks were archived for this date.</p>
                </div>
                <p className="text-blue-800 text-sm">
                  Try{" "}
                  <Link to="/analysis/today" className="text-[#F30099] underline-offset-2 underline">
                    today&apos;s analysis
                  </Link>
                  , explore by{" "}
                  <Link to="/analysis" className="text-[#F30099] underline-offset-2 underline">
                    state and track
                  </Link>
                  , or review the interactive{" "}
                  <Link to="/results" className="text-[#F30099] underline-offset-2 underline">
                    results dashboard
                  </Link>
                  .
                </p>
              </div>
            </Card>
          ) : (
            <div className={isArchiveFullWidth ? "w-screen relative left-1/2 right-1/2 -mx-[50vw] px-2 sm:px-4" : ""}>
              <div className="bg-white border border-gray-200 rounded-xl p-4 xs:p-6 shadow-lg space-y-4">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      Historical Results Archive
                    </h3>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        checked={showMaxHedge}
                        onChange={(e) => setShowMaxHedge(e.target.checked)}
                        className="text-[#F30099] focus:ring-[#F30099]"
                      />
                      <span>Show combined odds</span>
                    </label>

                    {showMaxHedge && (
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-700">Win Rate:</label>
                        <input
                          type="number"
                          value={maxHedgeWinRate}
                          onChange={(e) => setMaxHedgeWinRate(parseFloat(e.target.value) || 0.8)}
                          className="w-20 px-2 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#F30099] focus:border-transparent text-sm"
                          step="0.1"
                          min="0"
                          max="1"
                        />
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={handleDownloadCsv}
                      disabled={!visibleArchiveRaces?.length && !archiveRows.length}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors focus:ring-2 focus:ring-[#F30099] focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Download the currently shown rows as CSV"
                    >
                      <Download className="w-4 h-4" />
                      Download CSV
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsArchiveFullWidth((prev) => !prev)}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors focus:ring-2 focus:ring-[#F30099] focus:border-transparent"
                      title={isArchiveFullWidth ? "Collapse to normal width" : "Expand to full width"}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        {isArchiveFullWidth ? (
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                          />
                        ) : (
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                          />
                        )}
                      </svg>
                      {isArchiveFullWidth ? "Collapse" : "Expand"}
                    </button>
                  </div>
                </div>
                <ResultsArchiveTable
                  archiveData={archiveData}
                  isLoading={isLoading}
                  error={displayError}
                  trackName="All Tracks"
                  showEarlyMailTips={shouldShowEarlyMailTips && hasEarlyMailTips}
                  earlyMailTipsLookup={earlyMailTipsLookup}
                  hideDateColumn
                  showMaxHedge={showMaxHedge}
                  setShowMaxHedge={setShowMaxHedge}
                  maxHedgeWinRate={maxHedgeWinRate}
                  setMaxHedgeWinRate={setMaxHedgeWinRate}
                  onFilteredRacesChange={setVisibleArchiveRaces}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

